import React, { useState, useCallback, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  makeStyles,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  withStyles
} from '@material-ui/core';
import { useNotify, FormDataConsumer, useRefresh, Loading } from 'react-admin';
import { saveAs } from 'file-saver';
import { get } from 'lodash';

import WireInstructionApi from '../../../core/apis/WireInstructionApi';
import { ExcelIcon } from '../../../core/themes/icons/ExcelIcon';
import DialogTitleWithCloseButton from '../../../core/components/DialogTitleWithCloseButton';
import FileField from '../../../core/components/forked/field/FileField';
import FileInput from '../../../core/components/forked/input/FileInput';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import GridPopover from '../../../core/components/grid/GridPopover';
import CustomSimpleForm from '../../../core/components/form/CustomSimpleForm';
import ButtonMUI from '@material-ui/core/Button';

const useStyles = makeStyles({
  root: {
    flexGrow: 1
  },
  barTitle: {
    flexGrow: 1,
    paddingLeft: '8px'
  },
  horisontalRule: {
    height: '1px',
    backgroundColor: '#E0E0E0',
    border: 'none'
  },
  ButtonMUISpan: {
    paddingLeft: '4px'
  },
  excelIcon: {
    verticalAlign: 'middle',
    paddingRight: '10px'
  },
  paperWidthFalse: {
    height: 'inherit',
    width: '100%'
  },
  leftWrapper: {
    flexGrow: 1
  },
  validateButton: {
    textAlign: 'center',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)'
  },
  validateButtonText: {
    color: 'grey'
  },
  validationColumn: {
    color: 'red'
  },
  noRows: {
    color: 'red'
  },
  tableWrapper: {
    maxHeight: '70vh',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px'
    },
    /* Track */
    '&::-webkit-scrollbar-track': {
      background: '#ebebeb',
      borderRadius: '10px',
      margin: '2px'
    },
    /* Handle */
    '&::-webkit-scrollbar-thumb': {
      background: '#9E9E9E',
      borderRadius: '10px'
    },
    /* Handle on hover */
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#7b7a7a'
    }
  },
  table: {
    overflow: 'auto',
    maxHeight: 'calc(100vh - 364px)',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px'
    },
    /* Track */
    '&::-webkit-scrollbar-track': {
      background: '#ebebeb',
      borderRadius: '10px',
      margin: '2px'
    },
    /* Handle */
    '&::-webkit-scrollbar-thumb': {
      background: '#9E9E9E',
      borderRadius: '10px'
    },
    /* Handle on hover */
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#7b7a7a'
    }
  }
});

const ExcelIconImage = () => {
  const classes = useStyles();
  return <ExcelIcon className={classes.excelIcon}> </ExcelIcon>;
};

const StyledFileInput = withStyles({
  dropZone: {
    background: '#ffffff',
    border: '1px dashed #E0E0E0'
  },
  removeButton: {
    textAlign: 'center',
    padding: '.5rem'
  }
})(FileInput);

const columnSort = (colName, isDesc) => {
  return (a, b) => {
    const x = (get(a, colName) || '').toString().toLowerCase();
    const y = (get(b, colName) || '').toString().toLowerCase();
    if (isDesc) {
      return x < y ? -1 : x > y ? 1 : 0;
    } else {
      return x < y ? 1 : x > y ? -1 : 0;
    }
  };
};

const colDefsToMap = colDefs => {
  const res = {};
  colDefs.forEach(col => {
    res[col.id] = {
      idx: col.index,
      name: col.title
    };
  });
  return res;
};

const rowIndexCol = 'rowIndex';
const getColClass = (col, classes) =>
  col === 'validationErrors' ? classes.validationColumn : null;

const displayArrayInCell = (content, title, buttonClass) => {
  if (content && content.length) {
    return (
      <GridPopover
        text={content.length > 1 ? `${content.length} ${title}` : content[0]}
        classes={{ button: buttonClass }}
        multiple={content.length > 1}
        popoverContent={
          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{title}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {content.map((val, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell>{val}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        }
      />
    );
  } else {
    return null;
  }
};

const WireInstructionsImportFromExcelInterrnal = ({
  entityId,
  hasImportWireInstructionExcelPermission,
  hasExportWireInstructionExcelPermission,
  dataAvailable
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [importModelViewToggle, setsViewOfImport] = useState(false);
  const [validateButtonVisibile, setValidateButtonVisibility] = useState(false);
  const [disableImportButton, setImportButtonDisabled] = useState(true);
  const [loaderVisible, setLoaderVisible] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [showNoDataInExcelError, setShowNoDataInExcelError] = useState(false);
  const [isFileFieldDisabled, setIsFileFieldDisabled] = useState(false);

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState(-1);
  const [data, setData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [excelPayload, setExcelPayload] = useState(null);

  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const refresh = useRefresh();

  const colToColName = useMemo(() => {
    return colDefsToMap(columnDefs);
  }, [columnDefs]);

  const getOrderedColumns = useCallback(
    cols => {
      const foundCols = [];
      const notFoundCols = [];
      cols.forEach(col => {
        if (colToColName[col]) {
          foundCols[colToColName[col].idx] = col;
        } else {
          notFoundCols.push(col);
        }
      });
      const resCols = [];
      foundCols.forEach(col => resCols.push(col));
      resCols.push(...notFoundCols);
      return resCols;
    },
    [colToColName]
  );

  const columns = useMemo(() => {
    return getOrderedColumns(columnDefs.map(col => col.id));
  }, [columnDefs, getOrderedColumns]);

  const getColName = useCallback(
    col => (colToColName[col] && colToColName[col].name) || col,
    [colToColName]
  );

  const handleClick = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleImportClick = useCallback(() => {
    setAnchorEl(null);
    setsViewOfImport(true);
  }, []);

  const cleanUpData = useCallback(value => {
    setShowGrid(false);
    setImportButtonDisabled(true);
    setShowNoDataInExcelError(false);
    if (value) {
      setValidateButtonVisibility(true);
    } else {
      setValidateButtonVisibility(false);
    }
  }, []);

  const handleCloseImportClick = useCallback(() => {
    setAnchorEl(null);
    cleanUpData();
    setsViewOfImport(false);
  }, [cleanUpData]);

  const handleRequestSort = useCallback(
    index => {
      const isDesc = order === 'desc';
      setOrder(isDesc ? 'asc' : 'desc');
      setOrderBy(index);

      setData(data => data.sort(columnSort(columns[index], isDesc)));
    },
    [order, columns]
  );

  const handleClickValidation = useCallback(
    async values => {
      const payload = new FormData();
      if (values.file) {
        payload.append('excelFile', values.file.rawFile, values.file.title);
      }

      setValidateButtonVisibility(false);
      setImportButtonDisabled(true);
      setLoaderVisible(true);
      setIsFileFieldDisabled(true);
      try {
        const responseData = await WireInstructionApi.validateImportFromExcel(
          payload
        );
        setData(responseData.data);

        if (Array.isArray(responseData.columnDefinitions)) {
          setColumnDefs(responseData.columnDefinitions);
        } else {
          setColumnDefs([]);
        }
        if (Array.isArray(responseData.data) && responseData.data.length) {
          const validationErrors = responseData.data.some(
            elt => elt.validationErrors != null
          );

          setImportButtonDisabled(validationErrors);
        } else {
          setImportButtonDisabled(true);
          setShowNoDataInExcelError(true);
        }

        setLoaderVisible(false);
        setIsFileFieldDisabled(false);
        setShowGrid(true);
        setExcelPayload(payload);
      } catch (e) {
        setExcelPayload(null);
        setColumnDefs([]);
        setLoaderVisible(false);
        setIsFileFieldDisabled(false);
        notifyServerError(e);
      }
    },
    [notifyServerError]
  );

  const importWireInstructions = useCallback(
    async file => {
      try {
        await WireInstructionApi.importFromExcel(file);
        notify('ra.notification.custom_updated_message');
      } catch (error) {
        notifyServerError(error);
      }
    },
    [notify, notifyServerError]
  );

  const handleImportWireInstructionsClick = useCallback(async () => {
    if (!excelPayload) {
      return;
    }

    setImportButtonDisabled(true);
    setLoaderVisible(true);
    setShowGrid(false);
    setIsFileFieldDisabled(true);

    try {
      await importWireInstructions(excelPayload);
      setShowGrid(false);
      setLoaderVisible(false);
      setsViewOfImport(false);
      setIsFileFieldDisabled(false);
      refresh();
    } catch (e) {
      setLoaderVisible(false);
      notifyServerError(e);
    }
  }, [excelPayload, importWireInstructions, notifyServerError, refresh]);

  const handleExportClick = useCallback(async () => {
    setAnchorEl(null);
    WireInstructionApi.getWireInstructionsAsExcel(entityId)
      .then(resp => {
        const fileName = 'Wire Instructions.xlsx';
        saveAs(resp, fileName);
      })
      .catch(notifyServerError);
  }, [entityId, notifyServerError]);

  /**
   * Click on button 'Download as Excel' to download the excel file
   */
  const handleClickDownloadAsExcel = useCallback(async () => {
    setAnchorEl(null);
    WireInstructionApi.getWireInstructionsExcelTemplate()
      .then(resp => {
        const fileName = 'Wire Instructions Import Template.xlsx';
        saveAs(resp, fileName);
      })
      .catch(notifyServerError);
  }, [notifyServerError]);

  const [excelMenu, menuTitle] = useMemo(() => {
    const res = [];
    const title = [];
    if (hasImportWireInstructionExcelPermission) {
      res.push(
        <MenuItem onClick={handleImportClick} key="import">
          Import Wire Instructions
        </MenuItem>,
        <hr className={classes.horisontalRule} key="sep1" />,
        <MenuItem onClick={handleClickDownloadAsExcel} key="template">
          Download Wire Instructions Template
        </MenuItem>
      );

      title.push('Wire Instructions');
    }

    if (hasExportWireInstructionExcelPermission && dataAvailable) {
      res.push(
        <MenuItem onClick={handleExportClick} key="export">
          Export to Excel
        </MenuItem>
      );
      title.push('Export Wire Instructions');
    }
    return [res, title.join(' ')];
  }, [
    classes.horisontalRule,
    handleClickDownloadAsExcel,
    handleExportClick,
    handleImportClick,
    hasExportWireInstructionExcelPermission,
    hasImportWireInstructionExcelPermission,
    dataAvailable
  ]);

  if (!excelMenu.length) {
    return null;
  }

  return (
    <>
      <ButtonMUI
        aria-controls="simple-menu"
        aria-haspopup="true"
        onClick={handleClick}
      >
        <div className={classes.WrapperClass}>
          <ExcelIconImage />
          <span className={classes.ButtonMUISpan}>{menuTitle}</span>
        </div>
      </ButtonMUI>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={!!anchorEl}
        onClose={handleClose}
      >
        {excelMenu}
      </Menu>

      <Dialog
        open={importModelViewToggle}
        aria-label="Import WireInstructions from Excel"
        maxWidth={false}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return null;
          if (reason === 'escapeKeyDown') return handleCloseImportClick();
        }}
        classes={{
          paperWidthFalse: classes.paperWidthFalse
        }}
      >
        <DialogTitleWithCloseButton onClose={handleCloseImportClick}>
          Import Wire Instructions from Excel
        </DialogTitleWithCloseButton>

        <DialogContent>
          <div>
            <CustomSimpleForm toolbar={null} variant="standard" margin="normal">
              <FormDataConsumer>
                {({ formData }) => (
                  <Grid>
                    {!isFileFieldDisabled && (
                      <StyledFileInput
                        source="file"
                        label="Import File *"
                        accept={
                          'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        }
                        placeholder="Drag and drop File Here or Upload"
                        variant="standard"
                        onChange={value => cleanUpData(value)}
                      >
                        <FileField
                          source="src"
                          title="title"
                          size="size"
                          type="type"
                        />
                      </StyledFileInput>
                    )}
                    {loaderVisible && <Loading />}
                    {validateButtonVisibile && (
                      <div className={classes.validateButton}>
                        <p className={classes.validateButtonText}>
                          No Data To Display
                          <br />
                          Click 'Validate' to parse and display Excel Data
                        </p>
                        <Button
                          onClick={() => {
                            handleClickValidation(formData);
                          }}
                          color="primary"
                          variant="contained"
                        >
                          Validate
                        </Button>
                      </div>
                    )}

                    {showGrid && (
                      <div className={classes.root}>
                        <div className={classes.tableWrapper}>
                          <Table
                            stickyHeader
                            aria-label="sticky table"
                            size="small"
                            className={classes.table}
                          >
                            <TableHead>
                              <TableRow>
                                {columns.map((col, index) => (
                                  <TableCell
                                    className={classes.cell}
                                    key={col}
                                    sortDirection={
                                      orderBy === index ? order : false
                                    }
                                  >
                                    <TableSortLabel
                                      active={orderBy === index}
                                      direction={order}
                                      onClick={() => handleRequestSort(index)}
                                    >
                                      {getColName(col)}
                                    </TableSortLabel>
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {data.map(item => (
                                <TableRow
                                  key={get(item, rowIndexCol, '')}
                                  hover
                                >
                                  {columns.map(col => {
                                    let content = get(item, col, '');
                                    if (Array.isArray(content)) {
                                      content = displayArrayInCell(
                                        content,
                                        getColName(col),
                                        getColClass(col, classes)
                                      );
                                    }
                                    return (
                                      <TableCell
                                        key={
                                          col + ':' + get(item, rowIndexCol, '')
                                        }
                                        className={getColClass(col, classes)}
                                      >
                                        {content}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                        {showNoDataInExcelError && (
                          <span className={classes.noRows}>
                            There are no data in excel file to be validated.
                            Please, provide excel file with data.
                          </span>
                        )}
                      </div>
                    )}
                  </Grid>
                )}
              </FormDataConsumer>
            </CustomSimpleForm>
          </div>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid rgba(0, 0, 0, 0.12)` }}>
          <div className={classes.leftWrapper}>
            <Button
              onClick={handleCloseImportClick}
              color="primary"
              variant="outlined"
              className={classes.button}
            >
              Close
            </Button>
          </div>
          <Button
            onClick={handleImportWireInstructionsClick}
            color="primary"
            variant="contained"
            className={classes.button}
            disabled={disableImportButton}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const WireInstructionsImportFromExcel = props =>
  props.hasImportWireInstructionExcelPermission ||
  props.hasExportWireInstructionExcelPermission ? (
    <WireInstructionsImportFromExcelInterrnal {...props} />
  ) : null;

export default WireInstructionsImportFromExcel;
